import {
  buildGeocoderLocationBias,
  getGeocoderQueryVariants,
  rankFeaturesByProximity,
  removeFunctionalInterceptors,
  GEOCODER_LOCATION_BIAS_SCALE
} from 'js/lib/planner/search-location'

// Photon is asked for more candidates than the dropdown shows: the exact-name
// matches it ranks first are re-ranked by proximity client side (COEGIS1-15259).
// The caller still slices to its display limit.
export const GEOCODER_FETCH_LIMIT = 50

// Photon is a public, rate limited service (COEGIS1-15259): never more than
// GEOCODER_MAX_CONCURRENT_REQUESTS requests in flight, successful responses are
// kept GEOCODER_CACHE_TTL_MS in memory and a new search aborts the previous one.
export const GEOCODER_MAX_CONCURRENT_REQUESTS = 2
export const GEOCODER_CACHE_TTL_MS = 60 * 1000
export const GEOCODER_CACHE_MAX_ENTRIES = 50

const responseCache = new Map()
const searchControllers = new Map()
const pendingTasks = []
let activeRequests = 0

export function clearGeocoderCache() {
  responseCache.clear()
}

const readCache = url => {
  const entry = responseCache.get(url)

  if (!entry) return undefined

  if (Date.now() - entry.timestamp >= GEOCODER_CACHE_TTL_MS) {
    responseCache.delete(url)

    return undefined
  }

  return entry.response
}

const writeCache = (url, response) => {
  responseCache.delete(url)
  responseCache.set(url, { response, timestamp: Date.now() })

  while (responseCache.size > GEOCODER_CACHE_MAX_ENTRIES) {
    responseCache.delete(responseCache.keys().next().value)
  }
}

const drainQueue = () => {
  while (activeRequests < GEOCODER_MAX_CONCURRENT_REQUESTS && pendingTasks.length) {
    const task = pendingTasks.shift()

    if (!task.signal.aborted) task.run()
  }
}

/*
 * Cached, concurrency limited and abortable Vue.http.get. When the signal is
 * aborted the returned promise never settles: the caller is superseded, so it
 * must neither show an error nor overwrite newer results.
 */
const limitedGet = (url, requestOptions, signal) => {
  const cached = readCache(url)
  if (cached !== undefined) return Promise.resolve(cached)

  return new Promise((resolve, reject) => {
    const run = () => {
      activeRequests += 1
      let httpRequest = null
      let released = false
      const slot = {}
      const release = () => {
        if (released) return
        released = true
        signal.removeEventListener('abort', slot.onAbort)
        activeRequests -= 1
        drainQueue()
      }
      slot.onAbort = () => {
        if (httpRequest && typeof httpRequest.abort === 'function') httpRequest.abort()
        release()
      }
      signal.addEventListener('abort', slot.onAbort)

      const userBefore = requestOptions && requestOptions.before
      const options = {
        ...requestOptions,
        before(request) {
          httpRequest = request
          if (typeof userBefore === 'function') userBefore.call(this, request)
        }
      }

      // Photon is a third party host: the app interceptors (auth headers) must not be applied,
      // also when the request starts later from the queue.
      const { interceptors } = Vue.http
      const removed = Array.isArray(interceptors) ? removeFunctionalInterceptors(interceptors) : []
      let pending

      try {
        pending = Vue.http.get(url, options)
      } catch (error) {
        release()
        reject(error)

        return
      } finally {
        if (removed.length) interceptors.push(...removed)
      }

      Promise.resolve(pending).then(response => {
        release()
        if (signal.aborted) return
        writeCache(url, response)
        resolve(response)
      }, error => {
        release()
        if (signal.aborted) return
        reject(error)
      })
    }

    if (signal.aborted) return
    pendingTasks.push({ signal, run })
    drainQueue()
  })
}

/*
 * Aborts the in-flight and queued Photon requests of the last search started with
 * searchKey, or of every search when no key is given.
 */
export function cancelGeocoderSearches(searchKey) {
  const keys = searchKey === undefined ? Array.from(searchControllers.keys()) : [searchKey]
  keys.forEach(key => {
    const controller = searchControllers.get(key)

    if (controller) controller.abort()
    searchControllers.delete(key)
  })
}

const getFeatures = response => {
  const features = response && response.body && response.body.features
  return Array.isArray(features) ? features : []
}

const featureKey = ({ properties = {}, geometry }) => {
  if (properties.osm_type && properties.osm_id != null) {
    return `${properties.osm_type}:${properties.osm_id}`
  }
  const [lon, lat] = (geometry && geometry.coordinates) || []
  return `${properties.name}|${Math.round(lon * 1e4)}|${Math.round(lat * 1e4)}`
}

const mergeFeatures = lists => {
  const seen = new Set()
  return lists.reduce((all, list) => all.concat(list), []).filter(feature => {
    const key = featureKey(feature)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/*
 * Shared Photon pipeline of the address and points-of-interest searches:
 * query variants (ayuntamiento <-> ajuntament) fired in parallel, merged,
 * deduplicated and ranked by proximity to the bias point. Returns a
 * response-like copy with the re-ranked body.features.
 *
 * - extraParams: query params appended before limit (e.g. ['osm_tag=amenity']);
 *   every request keeps all of them.
 * - requestOptions: optional second argument of Vue.http.get (e.g. { skipLoading }).
 * - searchKey: searches sharing a key abort each other (a new one cancels the previous).
 * - Without a valid bias only q is sent (single request, response untouched).
 */
export function searchGeocoderRanked(params, { extraParams = [], requestOptions, searchKey = 'default' } = {}) {
  // A new search supersedes the previous one of the same kind (address / poi).
  cancelGeocoderSearches(searchKey)

  const controller = new AbortController()
  searchControllers.set(searchKey, controller)
  const { signal } = controller

  const variants = getGeocoderQueryVariants(params.paramsFilter)

  // Los bounds vienen de municipalityBounds (topology_municipalities). Algunos
  // municipios tienen las esquinas con longitudes intercambiadas, por lo que
  // bbox/lat/lon se calculan con min/max y media aritmetica (nunca getCenter()).
  // Si bbox es nulo o es un punto/linea (coordenadas null en base de datos)
  // no se filtra, ya que debe ser un rectangulo.
  const locationBias = buildGeocoderLocationBias(params.bbox, {
    userPosition: params.userPosition,
    municipalityCenter: { lat: params.lat, lng: params.lon }
  })

  const limit = Math.max(Number(params.limit) || 0, GEOCODER_FETCH_LIMIT)

  const buildUrl = (query, withBias) => {
    let url = `${params.routerUrl}?q=${query}`
    if (withBias) {
      url += `&bbox=${locationBias.bbox}&lat=${locationBias.lat}&lon=${locationBias.lon}` +
        `&location_bias_scale=${GEOCODER_LOCATION_BIAS_SCALE}` +
        extraParams.map(param => `&${param}`).join('') + `&limit=${limit}`
    }
    return url.replace(/ /g, '+')
  }

  const request = url => limitedGet(url, requestOptions, signal)

  // Sin sesgo valido (ni bounds ni centro valido) solo se envia q: nunca lat/lon sueltos.
  if (locationBias == null) {
    return request(buildUrl(variants[0], false)).then(response => response)
  }

  const first = request(buildUrl(variants[0], true))
  // El sinonimo (ayuntamiento <-> ajuntament) es un extra: si falla no rompe la busqueda.
  const second = variants.length > 1
    ? request(buildUrl(variants[1], true)).then(getFeatures, () => [])
    : Promise.resolve([])

  return Promise.all([first, second]).then(([response, extraFeatures]) => {
    if (!response || !response.body || !Array.isArray(response.body.features)) return response

    const features = rankFeaturesByProximity(
      mergeFeatures([response.body.features, extraFeatures]), locationBias, variants)
    const merged = Object.assign(Object.create(Object.getPrototypeOf(response)), response)
    merged.body = { ...response.body, features }
    if (Object.prototype.hasOwnProperty.call(response, 'data')) merged.data = merged.body
    return merged
  })
}
